/**
 * "This costs you a LOCALE slot" — who gets told, and why only them.
 *
 * The locale quota is a REDEMPTION LEDGER, not a live count of what a school
 * owns: creating a locale spends one unit of `school_subscription_eapck_quota`
 * permanently, duplicating one spends another, and deleting a locale gives
 * nothing back (see composer_v2/docs/locale-subscription-quota.md). Nothing in
 * the UI said so. A teacher who made three drafts, kept one and deleted two had
 * spent three of their allowance and could see only one locale for it — and the
 * first they heard of it was the "Locale Limit Reached" dialog, by which point
 * the slots were gone and no amount of tidying up brought them back.
 *
 * WHY THIS IS SCOPED TO eLAFP BASIC RATHER THAN SHOWN TO EVERYONE. The warning
 * is only true where the allowance is small enough to run out, and it is only
 * WELCOME where it is true: pinning a red line to every create dialog in every
 * school teaches people to stop reading red lines. The eLAFP 26/27 Basic tier
 * is the plan most Hong Kong schools now hold and the one with the tightest
 * MAX_LOCALE_NUMBER, so that is where a spent slot actually hurts. Its sibling
 * tier (`優越版` / `Premium`) is deliberately NOT matched.
 *
 * MATCHED ON THE SUBSCRIPTION NAME, not on a subscription id. The 26/27 rollout
 * gave every school its OWN cloned `subscription` row (39–75, from templates 34
 * and 35), so there is no one id to test, and a 27/28 round will clone a fresh
 * set again. Names follow `{school} eLAFP26/27 ({plan})`:
 *
 *     黃藻森學校 eLAFP26/27 (基本版)   /   Wong Cho Sum School eLAFP26/27 (Basic)
 *     eLAFP(26/27) 基本版              /   eLAFP(26/27) Basic          (templates)
 *
 * so both halves of the test live in the name. Both language columns are read
 * as ONE haystack per row, because a row carries the tier in whichever column
 * the importer filled: the Chinese name says `基本版`, the English one `Basic`,
 * and `render_name` falls back to the Chinese school name when a school has no
 * English one — leaving rows like `測試中學 eLAFP26/27 (Premium)` where the two
 * languages are mixed in a single string.
 *
 * This is the same reasoning as tools/subscriptionKeyword.js (the admin School
 * List's eLAFP filter) and deliberately a SEPARATE module: that one answers
 * "which schools hold an eLAFP grant of any tier" for an admin, this one answers
 * "should THIS teacher be warned before spending a slot". They match different
 * things — this one would be wrong to widen to Premium, that one would be wrong
 * to narrow to Basic — and the 2024 QEF round is out of both.
 *
 * The notice is advisory only. Nothing here blocks a write: the quota gates that
 * do are `canCreateLocale` (tools/localeSubscriptionGate.js) and the server's
 * own redeem.
 */

/** The programme. Present in every 26/27 subscription name, either language. */
export const ELAFP_KEYWORD = 'eLAFP';

/** The TIER. `基本版` is not a substring of `優越版`, so Premium cannot match. */
export const BASIC_TIER_KEYWORDS = ['基本版', 'Basic'];

/**
 * One row's searchable name, both languages folded together and lowercased.
 *
 * Accepts the `get_subscriptions_by_school_id` shape the composer holds
 * (`subscription_name` / `subscription_name_en`), the raw `subscription` shape
 * (`name` / `name_en`), or a bare string, so a caller with only the plan-name
 * list can use it too.
 */
function subscriptionNameHaystack(subscription) {
  if (!subscription) {
    return '';
  }
  if (typeof subscription === 'string') {
    return subscription.toLowerCase();
  }
  const zh = subscription.subscription_name || subscription.name || '';
  const en = subscription.subscription_name_en || subscription.name_en || '';
  return `${zh}\n${en}`.toLowerCase();
}

function haystackCarriesAny(haystack, keywords) {
  return keywords.some((keyword) => {
    const needle = String(keyword || '').trim().toLowerCase();
    return needle !== '' && haystack.includes(needle);
  });
}

/** True when ONE subscription is an eLAFP grant AND is its Basic tier. */
export function isElafpBasicSubscription(subscription) {
  const haystack = subscriptionNameHaystack(subscription);
  if (haystack === '') {
    return false;
  }
  return haystackCarriesAny(haystack, [ELAFP_KEYWORD])
    && haystackCarriesAny(haystack, BASIC_TIER_KEYWORDS);
}

/**
 * True when the school holds such a plan. Per ROW, not across rows: a school
 * holding eLAFP Premium and some unrelated "Basic" pilot has not thereby
 * bought eLAFP Basic.
 */
export function holdsElafpBasicSubscription(subscriptions) {
  return Array.isArray(subscriptions) && subscriptions.some(isElafpBasicSubscription);
}

/**
 * Should the create / duplicate / delete reminders be shown?
 *
 * `subscriptionFlag` is `Config.SUBSCRIPTION_FLAG`: with enforcement off no
 * slot is spent by anything, so the warning would be a lie.
 *
 * Unknown means NO here — the opposite of `canWriteLocale`, and for the same
 * reason. That gate fails OPEN because failing closed costs a paying school its
 * editor; this one is a sentence of text, so an empty or unanswered
 * subscription list should print nothing rather than warn a school about a
 * deduction that may not apply to it.
 *
 * `subscriptions` are the school's CURRENTLY-ACTIVE rows — `/api/subscription/
 * bySchool` returns only those unless asked otherwise — so a lapsed eLAFP Basic
 * grant stops triggering this. That school cannot create a locale at all
 * (canCreateLocale refuses it first), so there is no deduction left to warn
 * about.
 */
export function shouldWarnLocaleQuota({ subscriptionFlag, subscriptions } = {}) {
  if (subscriptionFlag !== true) {
    return false;
  }
  return holdsElafpBasicSubscription(subscriptions);
}

/**
 * `get_epack_quota`'s "unlimited" answer (`_UNLIMITED` in the backend's
 * `generic_crud_service.py`). It means "enforcement does not apply to this
 * school", never "99999 slots left", so it must never be printed as a number.
 */
export const QUOTA_UNLIMITED_SENTINEL = 99999;

/**
 * The two numbers the create / duplicate notices print — `{before, after}` — or
 * `null` when there is no honest pair to show.
 *
 * This is the RAW `epack_quotas` balance, deliberately NOT SubscriptionDialog's
 * clamped `remainingLocaleQuota`. That clamp exists only so the card's numerator
 * fits its denominator; this sentence has no denominator, it PREDICTS what the
 * server is about to do, and what the server decrements is the raw sum — the
 * same figure the composer's own refuse-gates already read
 * (`LocaleViewerBar.getEpackQuotaOK`, `ResourceLibraryPage`'s duplicate check).
 * Printing a clamped number here would let the sentence disagree with the button
 * beside it.
 *
 * `null` for three cases, each of which must fall back to the numberless
 * sentence rather than invent a figure:
 *
 * - **Unparseable or absent** — the request failed, or the field is missing.
 *   Note this cannot reuse the gates' `parseInt(raw, 10) || 0`: that idiom folds
 *   a failed parse into a confident `0`, which would print "from 0 to -1".
 * - **Zero or negative.** Both gates refuse to open a dialog at
 *   `availableQuota <= 0`, so a 0 arriving here is our own plumbing bug, not a
 *   school that is out of slots. Say nothing rather than "from 0 to -1".
 * - **The unlimited sentinel.** Structurally unreachable inside this module's
 *   audience gate — `shouldWarnLocaleQuota` needs a row in `activeSubscriptions`,
 *   which is filtered on `date_start <= CURDATE() <= date_expire`, the exact
 *   predicate that makes the backend's `_school_has_any_subscription` true and
 *   its 99999 branch unreachable — but guarded because the two conditions live
 *   in different repos and only one of them is tested here.
 */
export function localeQuotaFromTo(raw) {
  const remaining = parseInt(raw, 10);
  if (isNaN(remaining) || remaining <= 0 || remaining >= QUOTA_UNLIMITED_SENTINEL) {
    return null;
  }
  return { before: remaining, after: remaining - 1 };
}

/**
 * The notice's text: the numbered sentence when the balance is known, the
 * shipped numberless one otherwise.
 *
 * `String.replace`, not `Localization.strings.formatString`, on purpose. The
 * template here is a `Localization.strings` lookup, which returns `undefined`
 * for a key that has not shipped yet (see the Proxy in Localization.jsx), and
 * react-localization 1.0.19 formats that as `(str || '').split(...)` — i.e. it
 * answers `""`. Handed straight to a render it would erase the whole warning
 * silently, which is worse than losing the figure. Selecting the template FIRST
 * and falling back on a falsy one is what actually prevents that; `replace` then
 * keeps the substitution itself incapable of throwing, and matches the dominant
 * idiom in these files. (`formatString` does throw, but on a null ARGUMENT, not
 * a null template — checked against the repo's own node_modules.)
 */
export function localeQuotaNoticeText(plain, withQuota, raw) {
  const pair = localeQuotaFromTo(raw);
  if (!pair || !withQuota) {
    return plain || '';
  }
  return String(withQuota)
    .replace('{0}', String(pair.before))
    .replace('{1}', String(pair.after));
}

export default shouldWarnLocaleQuota;
